import { useTranslate } from '@tolgee/react';
import { Button, Form, InputNumber, message, Modal } from 'antd';
import FormField from 'components/FormField';
import { Formik } from 'formik';
import useModal from 'hooks/useModal';
import { tradeOfferV1 } from 'services/api/return';
import { formatNumber } from 'utils/numberFormat';

function OfferPartialRefund({
  unit,
  max,
  returnSn,
  platform,
  onSuccess,
}: {
  unit: string;
  max: number;
  returnSn: string;
  platform: General.PlatformUpperCase;
  onSuccess?: () => void;
}) {
  const { t } = useTranslate();

  const { visible, show, hide } = useModal();

  const button = (
    <Button type="link" onClick={show}>
      {t('scene.orderWorkspace.returnRefundTab.list.action.partialRefund')}
    </Button>
  );

  const modal = (
    <Formik
      initialValues={{ refundAmount: undefined }}
      onSubmit={({ refundAmount }, helpers) => {
        return tradeOfferV1({
          platform,
          returnExternalId: returnSn,
          refundAmount,
        } as any)
          .then(() => {
            onSuccess?.();
            hide();
            message.success(t('common.feedback.operationSuccess'));
            helpers.resetForm();
          })
          .catch(() => {
            message.error(t('common.feedback.operationFailedPleaseRetry'));
          });
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Modal
            title={t('scene.orderWorkspace.returnRefundTab.list.action.partialRefund')}
            width={300}
            visible={visible}
            onCancel={hide}
            okText={t('scene.orderWorkspace.returnRefundTab.action.submitPartialRefund')}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
          >
            <Form layout="vertical">
              <FormField
                required
                name="refundAmount"
                label={t('scene.orderWorkspace.returnRefundTab.detail.field.refundAmount')}
                extra={
                  <span style={{ color: 'var(--fett-surface-warning-text)' }}>
                    {t('scene.orderWorkspace.returnRefundTab.detail.field.maxRefundableAmount', {
                      money: `${unit}${formatNumber(max, '0,0.00')}`,
                    })}
                  </span>
                }
                validate={(value) => {
                  if (!value && value !== 0) {
                    return t(
                      'scene.orderWorkspace.returnRefundTab.validation.refundAmountRequired',
                    );
                  }
                  if (value > max) {
                    return t(
                      'scene.orderWorkspace.returnRefundTab.validation.exceedsMaxRefundableAmount',
                    );
                  }
                  if (value <= 0) {
                    return t(
                      'scene.orderWorkspace.returnRefundTab.validation.belowMinRefundableAmount',
                    );
                  }
                }}
              >
                {({ field }) => {
                  return (
                    <InputNumber
                      placeholder={t('common.placeholder.pleaseEnter')}
                      precision={2}
                      addonAfter={unit}
                      {...field}
                    />
                  );
                }}
              </FormField>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );

  return (
    <>
      {button}
      {modal}
    </>
  );
}

export default OfferPartialRefund;
